import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BACKEND_URL } from '../config/canonical';
import {
  ClipboardList, Calendar, MapPin, User, Clock, Search, RefreshCw, ExternalLink
} from 'lucide-react';

const CaseLogs = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCases(await res.json());
    } catch (e) {
      console.error('Fetch cases error:', e);
    }
  }, [token]);

  const fetchLogs = useCallback(async () => {
    if (!selectedCase) {
      setLogs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setLogs(await res.json());
    } catch (e) {
      console.error('Fetch logs error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCase]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = logs.filter(log => {
    if (!search) return true;
    const s = search.toLowerCase();
    return log.narrative?.toLowerCase().includes(s) || log.location_address?.toLowerCase().includes(s) || log.investigator_name?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="case-logs-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Case Logs</h1>
          <p className="text-slate-400">Field journal entries across investigations</p>
        </div>
        {selectedCase && (
          <Link to={`/cases/${selectedCase}`}>
            <Button variant="outline" className="border-slate-300 text-slate-300">
              <ExternalLink className="w-4 h-4 mr-2" /> View Case Detail
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={selectedCase} onValueChange={setSelectedCase}>
              <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="case-select">
                <SelectValue placeholder="Select a case..." />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search logs..." />
            </div>
            <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchLogs}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {!selectedCase ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Select a Case</h3>
            <p className="text-slate-400">Choose a case from the dropdown to view its field logs</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="text-center py-12 text-slate-400">Loading logs...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Logs Found</h3>
            <p className="text-slate-400">
              {search ? 'Try a different search term' : 'No log entries for this case yet'}
            </p>
            <Link to={`/cases/${selectedCase}`}>
              <Button className="mt-4 bg-[#0F172A] hover:bg-[#1E293B] text-white">
                Add Log from Case Detail
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(log => (
            <Card key={log.id} className="bg-white border-slate-200" data-testid={`log-card-${log.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      <span className="text-white font-medium">{log.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Clock className="w-4 h-4" />
                      {log.time_start}{log.time_end ? ` - ${log.time_end}` : ''}
                    </div>
                  </div>
                  <Badge variant="outline" className="border-slate-300 text-slate-400 text-xs">
                    {log.id}
                  </Badge>
                </div>
                {log.location_address && (
                  <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
                    <MapPin className="w-3 h-3" /> {[log.location_address, log.city, log.state].filter(Boolean).join(', ')}
                  </div>
                )}
                <p className="text-slate-200 text-sm whitespace-pre-wrap">{log.narrative}</p>
                {log.activity_observed && (
                  <p className="text-xs text-slate-400 mt-2">
                    <span className="text-slate-500">Activity:</span> {log.activity_observed}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                  <User className="w-3 h-3" /> {log.investigator_name}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseLogs;
